/* tslint:disable:max-file-line-count */
import { action, computed, extendObservable, observable, runInAction, transaction } from 'mobx';

import { AnalyticsStore, ContextStore, ExperimentStore, SerializedData, Store } from 'bernie-core';
import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';

import {
  CarouselModel,
  CustomerInterest,
  HotelResultsViewModel,
  HotelViewModel,
  LoyaltyEducation,
  LoyaltyMessagingModel,
  PrimaryPageMessagingModel,
  ReferrerInfoModel,
  RegionInfoModel,
  ResultTitleViewModel,
  RewardsDialogModel,
  SharedGuidsModel
} from 'models';

import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { STORE_EVENTS } from 'stores/events/store-events';
import {
  adaptFullPropertySearch,
  getPropertySearchInput,
  mapOmnitureData,
  mapPropertySearchResults,
  MigrationExperimentMapping,
  SearchMigrationAdapter,
  searchMigrationAdapters,
  searchMigrationAdaptorExecutor,
  searchMigrationExperimentMapping
} from 'stores/adapters';

import { HotelSearchApiSource } from 'sources/hotel-search-api-source';
import { BexApiSource } from 'sources/bex-api-source';
import { RateCalendarApiSource } from 'sources/rate-calendar-api-source';
import { FullPropertySearch, PropertyAlternateDestinations, PropertySearch, PwaHsrPropertyDestinationRefinement } from '__generated__/api';
import { OperationResult } from '__generated__/api/api-operations';
import { DateUtils } from 'utils/date-utils';

import { RateCalendarModel } from 'models/common/rate-calendar-model';
import { ResultType } from 'constants/resultType';
import { MultiItemContext } from 'models/multi-item/multi-item-types';
import { ExitIntentModel } from '/models/common/common-types';
import { PageData } from 'bernie-http';

export class SearchResultsStore extends Store {
  private PAST_PADDING: number = 90;
  private FUTURE_PADDING: number = 150;
  public static INITIAL_HOTEL_RENDER_COUNT = 20;
  public static HOTEL_TOTAL_RENDER_COUNT = 50;
  public static ALTERNATE_DESTINATIONS_UNAVAILABLE_INDEX = 3;

  constructor(
    private source: HotelSearchApiSource,
    private bexApiSource: BexApiSource,
    private rateCalendarSource: RateCalendarApiSource,
    state: SerializedData,
    logger: Logger = NOOP_LOGGER
  ) {
    super(state, logger);

    this.searchCriteria = new SearchCriteriaModel();

    this.fetch = this.fetch.bind(this);
    this.fetchAlternateDates = this.fetchAlternateDates.bind(this);
  }

  public hydrate(data: SerializedData): void {
    delete data.source;
    delete data.bexApiSource;

    this.searchCriteria.hydrate(data.searchCriteria || {});
    this.syncSearchDestination();
    delete data.searchCriteria;

    extendObservable(this, data);
  }

  public static DEFAULTS = {
    alternateDateFetchUrl: undefined,
    exitIntent: undefined,
    expandForm: false,
    foregoClientFetch: false,
    hotels: [],
    hotelsMaxDisplay: SearchResultsStore.INITIAL_HOTEL_RENDER_COUNT,
    insights: undefined,
    isLoading: true,
    loyaltyMessagingModel: undefined,
    messages: undefined,
    omnitureData: {},
    pageData: {},
    primaryPageMessagingModel: undefined,
    resultTitleModel: {},
    searchAndFilterDisplayOptions: {},
    searchedDestination: '',
    shouldFetch: true,
    sortAndFilter: {},
    totalPropertyCount: 0,
    universalDataObject: {}
  };

  @observable
  public alternateDateFetchUrl: string;
  @observable
  public alternateDestinations: PropertyAlternateDestinations.Query;
  @observable
  public destinationRefinement: PwaHsrPropertyDestinationRefinement.Query;
  @observable
  public error: any;
  @observable
  public errors: any[];
  @observable
  public exitIntent: ExitIntentModel;
  @observable
  public expandForm: boolean; // Set on client.
  @observable
  public foregoClientFetch: boolean;
  @observable
  public headerMessages: PropertySearch.HeaderMessages[];
  @observable
  public hotels: HotelViewModel[];
  @observable
  public hotelsMaxDisplay: number;
  @observable
  public insights: any;
  @observable
  public isLoading: boolean;
  @observable
  public loyaltyEducation: LoyaltyEducation;
  @observable
  public loyaltyMessagingModel: LoyaltyMessagingModel;
  @observable
  public map: any;
  @observable
  public messages: any;
  @observable
  public omnitureData: any;
  @observable
  public overFiltered: boolean;
  @observable
  public primaryPageMessagingModel: PrimaryPageMessagingModel;
  @observable
  public propertyTypeCarousel: CarouselModel;
  @observable
  public rates: RateCalendarModel;
  @observable
  public referrerInfo: ReferrerInfoModel;
  @observable
  public regionInfo: RegionInfoModel;
  @observable
  public resultTitleModel: ResultTitleViewModel;
  @observable
  public rewardsDialogContent: RewardsDialogModel;
  @observable
  public searchAndFilterDisplayOptions: any; // Set on server.
  @observable
  public searchedDestination: string;
  @observable
  public sharedGuids: SharedGuidsModel;
  @observable
  public shouldFetch: boolean;
  @observable
  public sortAndFilter: any;
  @observable
  public totalPropertyCount: number;
  @observable
  public universalDataObject: any;

  @observable.ref
  public pageData: PageData;
  @observable.ref
  public searchCriteria: SearchCriteriaModel;

  public defaultRequestFields: any;
  public bexApi: number = 0;
  public maxHotelsDisplayedPerLoad: number = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;

  @computed
  get isError(): boolean {
    return Boolean(!this.isLoading && (this.error || this.errors || this.overFiltered));
  }

  @computed
  get isEmpty(): boolean {
    return Boolean(!this.isLoading && (!this.hotels || this.hotels.length === 0));
  }

  @computed
  get displayedUnavailableCount(): number {
    if (this.hotels === null) {
      return 0;
    }

    const unavailable = this.filterHotels(this.hotels)
      .slice(0, this.hotelsMaxDisplay)
      .filter(hotel => !hotel.hasAvailableOffer);

    return unavailable !== undefined ? unavailable.length : 0;
  }

  @action
  public showMoreHotels(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    customerInterests: CustomerInterest[],
    multiItemContext: MultiItemContext
  ): Promise<number> {
    this.isLoading = true;

    return new Promise(resolve => {
      setTimeout(
        action(() => {
          const currentMax = this.hotelsMaxDisplay;
          const numHotelsFetched = this.filterHotels(this.hotels).length;

          if (this.hotelsMaxDisplay < numHotelsFetched) {
            this.hotelsMaxDisplay += this.maxHotelsDisplayedPerLoad;
          }

          if (
            this.hotelsMaxDisplay + this.maxHotelsDisplayedPerLoad >= numHotelsFetched && // is one batch away from the end
            numHotelsFetched < this.totalPropertyCount
          ) {
            this.fetchNextBatch(
              analyticsStore,
              context,
              customerInterests,
              numHotelsFetched,
              this.maxHotelsDisplayedPerLoad,
              multiItemContext
            );
          } else {
            this.isLoading = false;
          }

          resolve(currentMax);
        }),
        400
      );
    });
  }

  // Temporary helper function to exclude non-hotels returned by BFF from hotel list
  private filterHotels(hotels) {
    if (hotels === null || this.bexApi === 2) {
      return hotels;
    }

    return hotels.filter(hotel => hotel.type === 'HotelViewModel');
  }

  private fetchNextBatch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    customerInterests: CustomerInterest[],
    startingIndex: number,
    numToFetch: number,
    multiItemContext: MultiItemContext
  ): void {
    if (this.bexApi === 2) {
      const variables: FullPropertySearch.Variables = getPropertySearchInput(
        context,
        customerInterests,
        this.searchCriteria,
        this.sortAndFilter,
        multiItemContext
      );
      variables.searchPagination = { size: numToFetch, startingIndex };

      this.bexApiSource
        .fetchFullBexApiPropertySearchTemp(variables)
        .then(({ data, extensions }) => {
          transaction(() => {
            // isLoading needs to change with new omniture data in one step
            this.isLoading = false;
            this.hotels = this.hotels.concat(mapPropertySearchResults(data));
            this.omnitureData = mapOmnitureData(extensions.analytics);
          });
        })
        .catch(error => {
          this.isLoading = false;
          this.logger.logEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_STORE_FETCH_ERROR, getErrorString(error));
        });
    } else {
      const criteria = SearchQueryAdapter.parseOut({
        ...this.searchCriteria.toJSON(),
        count: numToFetch,
        startingIndex
      });

      this.source
        .fetch(context, criteria)
        .then(data => {
          if (data.hotels) {
            transaction(() => {
              // isLoading needs to change with new omniture data in one step
              this.hotels = this.hotels.concat(this.filterHotels(data.hotels));
              this.omnitureData = data.omnitureData;
              this.isLoading = false;
            });
          } else {
            this.isLoading = false;
          }
        })
        .catch(error => {
          this.isLoading = false;
          this.logErrorEvent(error);
        });
    }

    analyticsStore.publishClientSidePayload([this.omnitureData]);
  }

  @action
  public updateSearchCriteria(searchCriteria): void {
    this.searchCriteria = searchCriteria;
    this.propertyTypeCarousel = undefined;
    this.syncSearchDestination();
    this.shouldFetch = true;
  }

  @action
  public forceFetch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    experiments: ExperimentStore,
    additionalCriteria: any = {},
    multiItemContext: MultiItemContext
  ): Promise<any> {
    this.shouldFetch = true;

    return this.fetch(analyticsStore, context, [], experiments, additionalCriteria, multiItemContext);
  }

  public prepareFetch(
    context: ContextStore,
    combinedCriteria: SearchCriteriaModel,
    customerInterests: CustomerInterest[],
    multiItemContext: MultiItemContext
  ): {
    promises: Array<Promise<any>>;
    migrationAdapters: SearchMigrationAdapter[];
    migrationExperimentMapping: MigrationExperimentMapping;
  } {
    const promises: Array<Promise<any>> = new Array(2);
    let migrationAdapters: SearchMigrationAdapter[] = searchMigrationAdapters;
    let migrationExperimentMapping: MigrationExperimentMapping;

    if (this.bexApi !== 2) {
      promises[0] = this.source.fetch(context, combinedCriteria);
    }

    if (this.bexApi === 1) {
      migrationExperimentMapping = searchMigrationExperimentMapping;

      promises[1] = this.fetchBexApiPropertySearch(context, customerInterests, multiItemContext);
    }

    // For testing ONLY!
    if (this.bexApi === 2) {
      migrationAdapters = [adaptFullPropertySearch];

      promises[1] = this.fetchFullBexApiPropertySearchTemp(context, customerInterests, multiItemContext);
    }

    return { promises, migrationAdapters, migrationExperimentMapping };
  }

  @action
  public fetch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    customerInterests: CustomerInterest[],
    experiments: ExperimentStore,
    additionalCriteria: any = {},
    multiItemContext: MultiItemContext
  ): Promise<any> {
    if (this.shouldFetch === false) {
      return Promise.resolve(this);
    }

    this.shouldFetch = false;
    this.isLoading = true;
    this.error = null;
    this.hotels = [];
    this.insights = undefined;
    this.messages = undefined;

    const {
      PWA_Display_Destination_Refinement_Module,
      PWA_Destination_Refinement_Throttle,
      PWA_AltDestinations_HSR_Throttle,
      Alt_Destinations_HSR_Recommender
    } = experiments.exposures;
    const isInAlternateDestinationsThrottle = PWA_AltDestinations_HSR_Throttle && PWA_AltDestinations_HSR_Throttle.bucket === 1;
    const isInAlternateDestinationsExperiment = Alt_Destinations_HSR_Recommender && Alt_Destinations_HSR_Recommender.bucket !== 0;
    const isInDestinationRefinementThrottle = PWA_Destination_Refinement_Throttle && PWA_Destination_Refinement_Throttle.bucket === 1;
    const isInRefinedDestinationsVariant =
      PWA_Display_Destination_Refinement_Module && PWA_Display_Destination_Refinement_Module.bucket !== 0;

    const criteriaJson = SearchQueryAdapter.parseOut(this.searchCriteria && this.searchCriteria.toJSON());
    const combinedCriteria = { ...criteriaJson, ...additionalCriteria };

    this.alternateDestinations = null;
    if (isInAlternateDestinationsThrottle) {
      this.fetchAlternateDestinations(context, customerInterests);
    }

    this.destinationRefinement = null;
    if (isInDestinationRefinementThrottle) {
      this.fetchDestinationRefinement(context, customerInterests);
    }

    const { promises, migrationAdapters, migrationExperimentMapping } = this.prepareFetch(
      context,
      combinedCriteria,
      customerInterests,
      multiItemContext
    );

    return Promise.all(promises)
      .then(([bffResults, bexApiResults]) =>
        searchMigrationAdaptorExecutor(
          context,
          this.pageData,
          migrationAdapters,
          migrationExperimentMapping,
          experiments,
          bffResults,
          bexApiResults
        )
      )
      .then((data: HotelResultsViewModel) =>
        runInAction('successfully updating search results', () => {
          this.isLoading = false;

          if (data) {
            if (!data.hotels) {
              data.hotels = null;
            }

            if (isInAlternateDestinationsThrottle && isInAlternateDestinationsExperiment && !this.alternateDestinations) {
              this.logger.logEvent(STORE_EVENTS.BEX_API_ALTERNATE_DESTINATIONS_TIMEOUT);
            }

            if (isInDestinationRefinementThrottle && isInRefinedDestinationsVariant && !this.destinationRefinement) {
              this.logger.logEvent(STORE_EVENTS.BEX_API_DESTINATION_REFINEMENT_TIMEOUT);
            }

            const searchCriteria = this.searchCriteria.clone();
            const defaultRequestFields = data.defaultRequestFields;

            if (defaultRequestFields) {
              Object.assign(searchCriteria, defaultRequestFields);
            }

            const sortAndFilterSelected = data.sortAndFilter && data.sortAndFilter.selected;
            if (sortAndFilterSelected) {
              Object.assign(searchCriteria, sortAndFilterSelected);
            }

            if (data.exitIntent) {
              this.exitIntent = data.exitIntent;
            }

            this.searchCriteria = searchCriteria;

            Object.assign(this, data);
            this.setIntentMediaParams(data.intentMedia);

            analyticsStore.publishClientSidePayload([this.omnitureData]);
          }

          return data;
        })
      )
      .catch(err =>
        runInAction('error during search results fetch', () => {
          this.logErrorEvent(err);
          // don't assign the error to this.error, as errors in this catch are technical and NOT user friendly
          this.error = true;
          this.isLoading = false;
          this.shouldFetch = true;

          // recover and pass object with error details
          return {
            error: err
          };
        })
      );
  }

  @action
  public fetchAlternateDates(context, additionalCriteria: any = {}): Promise<any> {
    const promise = this.source.fetchAlternateDates(context, additionalCriteria);

    return promise
      .then(data =>
        runInAction('successfully updating alternate date on search results', () => {
          if (data && this.hotels) {
            this.hotels.forEach((hotel, index) => {
              if (data[hotel.id]) {
                this.hotels[index] = Object.assign({}, hotel, { alternateDates: data[hotel.id] });
              }
            });
          }

          return data;
        })
      )
      .catch(err =>
        runInAction('error during alternate date on search results fetch', () => {
          this.logErrorEvent(err);

          // don't assign the error to this.error, as errors in this catch are technical and NOT user friendly
          this.error = true;

          // recover and pass object with error details
          return {
            error: err
          };
        })
      );
  }

  public fetchHSRRates(l10n, requestStartDate, requestEndDate, adults, children, regionId, context) {
    const { startDate, endDate } = this.resolveSearchDates(l10n, requestStartDate, requestEndDate);

    return this.rateCalendarSource
      .fetchHSR(context, { startDate, endDate, adults, children }, regionId)
      .then(res => {
        if (res && res.rateCalendar) {
          this.rates = res.rateCalendar;
        } else {
          this.rates = {};
        }

        return this.rates;
      })
      .catch(error => {
        runInAction('rate calendar call failure', () => {
          this.logErrorEvent(error);
        });
      });
  }

  @action
  public clearRates() {
    this.rates = {};
  }

  private resolveSearchDates(l10n, requestStartDate, requestEndDate) {
    const dateUtils = new DateUtils(l10n);

    const today = dateUtils.getToday();
    const endDate = dateUtils.getNDaysFrom(requestEndDate, this.FUTURE_PADDING);
    const daysBetweenTodayAndRequestStartDate = dateUtils.getDaysBetween(today, requestStartDate);
    let startDate;

    if (daysBetweenTodayAndRequestStartDate <= this.PAST_PADDING && daysBetweenTodayAndRequestStartDate >= 0) {
      startDate = today;
    } else {
      startDate = dateUtils.getNDaysFrom(requestStartDate, -this.PAST_PADDING);
    }

    return { startDate, endDate };
  }

  private fetchBexApiPropertySearch(
    context: ContextStore,
    customerInterests: CustomerInterest[],
    multiItemContext: MultiItemContext
  ): Promise<OperationResult<PropertySearch.Query>> {
    const variables: PropertySearch.Variables = getPropertySearchInput(
      context,
      customerInterests,
      this.searchCriteria,
      this.sortAndFilter,
      multiItemContext
    );

    return this.bexApiSource.fetchBexApiPropertySearch(variables).then(({ data, extensions }) => ({ data, extensions }));
  }

  private fetchFullBexApiPropertySearchTemp(
    context: ContextStore,
    customerInterests: CustomerInterest[],
    multiItemContext: MultiItemContext
  ): Promise<OperationResult<PropertySearch.Query>> {
    const variables: PropertySearch.Variables = getPropertySearchInput(
      context,
      customerInterests,
      this.searchCriteria,
      this.sortAndFilter,
      multiItemContext
    );

    return this.bexApiSource.fetchFullBexApiPropertySearchTemp(variables).then(({ data, extensions }) => ({ data, extensions }));
  }

  private fetchAlternateDestinations(context: ContextStore, customerInterests: CustomerInterest[]): Promise<any> {
    const variables: PropertySearch.Variables = getPropertySearchInput(
      context,
      customerInterests,
      this.searchCriteria,
      this.sortAndFilter,
      null
    );

    return this.bexApiSource.fetchAlternateDestinations(variables).then(response => {
      const data = response && response.data;
      this.alternateDestinations = data;
    });
  }

  private fetchDestinationRefinement(context: ContextStore, customerInterests: CustomerInterest[]): Promise<any> {
    const variables: PropertySearch.Variables = getPropertySearchInput(
      context,
      customerInterests,
      this.searchCriteria,
      this.sortAndFilter,
      null
    );

    return this.bexApiSource.fetchDestinationRefinement(variables).then(response => {
      this.destinationRefinement = response && response.data;
    });
  }

  private syncSearchDestination() {
    if (this.searchCriteria.destination && this.searchCriteria.destination !== '') {
      this.searchedDestination = this.searchCriteria.destination;
    } else if (this.searchedDestination && this.searchedDestination !== '') {
      this.searchCriteria.destination = this.searchedDestination;
    }
  }

  private logErrorEvent(err) {
    if (this.logger) {
      this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(err));
    }
  }

  public findHotel(hotelId: string): HotelViewModel {
    return this.hotels.find(h => h.id && h.id.toString() === hotelId);
  }

  public setIntentMediaParams(intentMedia) {
    if (intentMedia) {
      const {
        city,
        countryCode,
        machineId,
        machineIdHashed,
        publisherSessionId,
        publisherSessionIdHashed,
        publisherUserIdHashed,
        referrerChannel,
        referrerSource,
        state
      } = intentMedia;
      this.sharedGuids = { machineId, machineIdHashed, publisherSessionId, publisherSessionIdHashed, publisherUserIdHashed };
      this.regionInfo = { city, state, countryCode };
      this.referrerInfo = { referrerChannel, referrerSource };
    }
  }

  @action
  public displaySearchButton() {
    this.map = Object.assign({}, this.map, {
      showSearchButton: true
    });
  }

  @action
  public dismissMapMessage() {
    this.map = Object.assign({}, this.map, {
      mapMessage: ''
    });
  }

  @action
  public clearDefaultRequestFields() {
    this.defaultRequestFields = {};
  }

  @action
  public setHotelsMaxDisplayToTotalRenderCount() {
    this.hotelsMaxDisplay = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;
  }

  // This method is used to mold non-bex api input into bex api input.
  // Can be removed once bex api is 100%
  public splitListings(listings, messages) {
    let hotelListings = [];
    let messageListings = [];
    if (messages && messages.length > 0) {
      hotelListings = listings;
      messageListings = messages;
    } else if (listings != null) {
      listings.forEach((listing, index) => {
        if (listing.resultType === ResultType.MESSAGING) {
          messageListings.push({ ...listing, index });
        } else {
          hotelListings.push(listing);
        }
      });
    }

    return { hotelListings, messageListings };
  }
}
